import { useRollbar } from '@rollbar/react';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { fetchSelector } from 'common/state/common';
import { chartStudyMapAtom } from 'common/state/charts';
import { Study } from 'common/types/account';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { LogArgument } from 'rollbar';
import { API_URLS } from 'settings/api';
import { useEffect, useMemo, useRef } from 'react';

const isChartStudyMapEqual = (left: Record<string, string>, right: Record<string, string>): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  for (const key of leftKeys) {
    if (left[key] !== right[key]) {
      return false;
    }
  }

  return true;
};

const buildChartStudyMapFromStudies = (studies: Array<Study>, rollbar: ReturnType<typeof useRollbar>): Record<string, string> => {
  const map: Record<string, string> = {};

  for (const study of studies) {
    for (const chartUuid of study.chart_uuids || []) {
      if (!chartUuid) {
        continue;
      }

      const previousStudyUuid = map[chartUuid];
      if (previousStudyUuid && previousStudyUuid !== study.uuid) {
        rollbar.info('useSeedChartStudyMapFromStudies duplicate chart membership', {
          chartUuid,
          previousStudyUuid,
          studyUuid: study.uuid,
        } as LogArgument);
      }

      map[chartUuid] = study.uuid;
    }
  }

  return map;
};

export const useGetAllStudies = (): UseQueryResult<Array<Study>, unknown> => {
  const { fetchGet } = useRecoilValue(fetchSelector);
  const rollbar = useRollbar();
  return useQuery(
    ['allStudies'],
    async () => {
      try {
        return await fetchGet<Study[]>(API_URLS.STUDIES);
      } catch (e: unknown) {
        rollbar.info('useGetAllStudies error', e as LogArgument);
      }
    },
    {
      staleTime: Infinity,
      placeholderData: [],
    },
  );
};

export const useSeedChartStudyMapFromStudies = () => {
  const rollbar = useRollbar();
  const setChartStudyMap = useSetRecoilState(chartStudyMapAtom);
  const { data: studies = [], isSuccess, isPlaceholderData } = useGetAllStudies();
  const hasSeededMapRef = useRef(false);

  const nextChartStudyMap = useMemo(() => buildChartStudyMapFromStudies(studies, rollbar), [studies, rollbar]);

  useEffect(() => {
    if (!isSuccess || isPlaceholderData || hasSeededMapRef.current) {
      return;
    }

    if (studies.length > 0 && Object.keys(nextChartStudyMap).length === 0) {
      rollbar.info('useSeedChartStudyMapFromStudies empty mapping snapshot');
    }

    setChartStudyMap(prevMap => {
      if (isChartStudyMapEqual(prevMap, nextChartStudyMap)) {
        return prevMap;
      }

      return nextChartStudyMap;
    });

    hasSeededMapRef.current = true;
  }, [isSuccess, isPlaceholderData, nextChartStudyMap, rollbar, setChartStudyMap, studies.length]);
};
